import React, { useState } from "react";
import Layout from "./Layout";
import {
  createInitialForm,
  validateRequiredField,
} from "../../../common/common";
import { loginData } from "../../../assets/lib/login";
import { COLLECTION, SESSION } from "../../../data/constant";
import {
  Timestamp,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../routing/routes";

const UserLogin = () => {
  const [loginState, setLoginState] = useState(createInitialForm(loginData));
  const [loginFailedText, setLoginFailedText] = useState("");
  const [errorFields, setErrorFields] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Email verification states
  const [email, setEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [generatedEmailOtp, setGeneratedEmailOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailInvalidOtp, setEmailInvalidOtp] = useState(false);
  const [invalidCompanyEmail, setInvalidCompanyEmail] = useState(false);
  const [campusDisabled, setCampusDisabled] = useState(false);

  const [currentUserDoc, setCurrentUserDoc] = useState(null);

  const navigate = useNavigate();

  const getLoginInput = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    const inputType = event.target.type;
    if (value && inputType === "number") {
      value = parseInt(value);
    }
    if (errorFields.includes(name) && ![undefined, null, ""].includes(value)) {
      setErrorFields((prev) => prev.filter((key) => key !== name));
    }
    setLoginState((prev) => ({ ...prev, [name]: value }));
  };

  /** ---------- Campus mapping (same as signup) ---------- */
  const getCampusByEmail = async (email) => {
    const userDomain = email?.split("@")[1]?.toLowerCase();
    if (!userDomain) return null;

    const campusSnap = await getDocs(collection(db, COLLECTION.CAMPUS));

    for (const campusDoc of campusSnap.docs) {
      const admins = campusDoc.data().campusAdmins || [];
      const matchedAdmin = admins.find(
        (admin) => admin.email?.split("@")[1]?.toLowerCase() === userDomain
      );

      if (matchedAdmin) {
        return {
          campusId: campusDoc.data().campusId,
          campusName: campusDoc.data().campusName,
          disabled: matchedAdmin.disabled === true,
        };
      }
    }
    return null;
  };

  /** ---------- Generate OTP ---------- */
  const generateOTP = () => Math.floor(1000 + Math.random() * 9000).toString();

  /** ---------- Send Email OTP ---------- */
  const handleSendEmailOtp = async () => {
    setInvalidCompanyEmail(false);
    setCampusDisabled(false);

    if (!email) {
      setErrorFields(["email"]);
      return;
    }

    const campus = await getCampusByEmail(email);

    if (!campus) {
      setInvalidCompanyEmail(true);
      return;
    }

    if (campus.disabled) {
      setCampusDisabled(true);
      return;
    }

    const otp = generateOTP();
    setGeneratedEmailOtp(otp);

    await fetch("https://sendemailotp-713732302795.asia-south1.run.app", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        otp,
        userName: currentUserDoc.userName,
      }),
    });
  };

  /** ---------- Verify Email OTP ---------- */
 /** ---------- Verify Email OTP ---------- */
const verifyEmailOtp = async () => {
  if (String(emailOtp) !== String(generatedEmailOtp)) {
    setEmailInvalidOtp(true);
    return;
  }

  const userDocRef = doc(db, COLLECTION.USER, currentUserDoc.id);

  // Get latest user data
  const userSnap = await getDoc(userDocRef);
  if (!userSnap.exists()) {
    throw new Error("User document not found");
  }

  const userData = userSnap.data();

  // Credit wallet only once (first-time email verification)
  if (!userData.emailRewardCredited) {
    const currentWalletAmount = Number(userData.walletAmount || 0);

    await updateDoc(userDocRef, {
      email,
      walletAmount: currentWalletAmount + 15,
      emailRewardCredited: true,
    });
  } else {
    // If reward already credited, just ensure email is saved
    if (!userData.email) {
      await updateDoc(userDocRef, { email });
    }
  }

  setEmailVerified(true);

  // Proceed to login
  await createSessionAndNavigate(
    { ...currentUserDoc, email },
    email
  );
};

  /** ---------- Create session & navigate ---------- */
  const createSessionAndNavigate = async (userDoc, verifiedEmail) => {
    const sessionCollectionRef = collection(db, COLLECTION.USER_SESSION);
    const addedDocRef = await addDoc(sessionCollectionRef, {
      phoneNumber: userDoc.phoneNumber,
      sessionStart: Timestamp.now(),
      userID: userDoc.id,
      email: verifiedEmail || userDoc.email,
    });

    const userDocRef = doc(db, COLLECTION.USER, userDoc.id);
    await updateDoc(userDocRef, { status: "Active" });

    localStorage.setItem(SESSION.USER_SESSION_ID, addedDocRef.id);
    // ✅ ADD This line
          const configSnap = await getDoc(doc(db, COLLECTION.CONFIG, "app"));
      localStorage.setItem(
        "SESSION_VERSION",
        String(configSnap.data().sessionVersion)
      );

    navigate(routes.SAVIRUCHIIMAGE, { state: { phoneNumber: userDoc.phoneNumber } });
  };

  /** ---------- Handle login ---------- */
  const handleLogin = async () => {
    const error = validateRequiredField(loginData, loginState);
    setErrorFields(error);

    if (error.length > 0) return;

    try {
      setSubmitLoading(true);
      const collectionRef = collection(db, COLLECTION.USER);
      const q = query(
        collectionRef,
        where("phoneNumber", "==", loginState.phoneNumber)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setSubmitLoading(false);
        setLoginFailedText("No phone number found");
        return;
      }

      const docSnap = querySnapshot.docs[0];
      const docData = { ...docSnap.data(), id: docSnap.id };
      setCurrentUserDoc(docData);

      if (docData.alpin !== loginState.alpin) {
        setSubmitLoading(false);
        setLoginFailedText("Incorrect ALPIN");
        return;
      }

      // ✅ Check if professional email exists
      if (!docData.email) {
        setSubmitLoading(false);
        return; // show email verification fields in UI
      }

      // ✅ Normal login if email exists
      await createSessionAndNavigate(docData);
    } catch (err) {
      console.log(err);
      setSubmitLoading(false);
    }
  };

  return (
    <Layout
      loginState={loginState}
      loginFailedText={loginFailedText}
      errorFields={errorFields}
      getLoginInput={getLoginInput}
      handleLogin={handleLogin}
      submitLoading={submitLoading}
      email={email}
      setEmail={setEmail}
      emailOtp={emailOtp}
      setEmailOtp={setEmailOtp}
      emailVerified={emailVerified}
      generatedEmailOtp={generatedEmailOtp}
      handleSendEmailOtp={handleSendEmailOtp}
      verifyEmailOtp={verifyEmailOtp}
      invalidCompanyEmail={invalidCompanyEmail}
      campusDisabled={campusDisabled}
      emailInvalidOtp={emailInvalidOtp}
      currentUserDoc={currentUserDoc}
    />
  );
};

export default UserLogin;
